import { FormikProvider, useFormik } from 'formik'
import { getFetchError } from '@/reducers/fetch'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'
import { dollarsToCents } from '@/utils/currencyHelpers'
import {
  getFinancialProfile,
  selectIsCurrentUserScorp,
} from '@/selectors/user.selectors'
import {
  UPDATE_FINANCIAL_PROFILE_KEY,
  updateFinancialProfile,
} from '@/actions/financialProfileActions'
import { Alert, Form, Text } from '@/ui'

import {
  TAX_PROFILE_STEP,
  TaxesProfileFlowAnswerProps,
  useTaxProfileSteps,
} from './helpers'
import { TaxProfileFormFooter } from './TaxProfileFormFooter'
import {
  TaxProfileAccordions,
  useTaxProfileAccordionCopy,
} from './TaxProfileAccordions'
import { TypographyStyles } from '@/styles'

// TODO: Migrate to V2
import {
  FormikLabelError,
  FormikRadioToggleButton,
  getFieldName,
  makeNumberSchema,
  makeReqBoolSchema,
} from '@/base'
import { useTherapistFlag, CaseStyle } from '@/utils/launchdarklyHelpers'

export const Ten99Income = ({
  formFlowAnswers,
  setFormFlowAnswers,
}: TaxesProfileFlowAnswerProps) => {
  const dispatch = useAppDispatch()
  const fp = useReselector(getFinancialProfile)
  const track = useAnalyticsTrack()
  const isScorp = useReselector(selectIsCurrentUserScorp)
  const error = useReselector(
    getFetchError,
    UPDATE_FINANCIAL_PROFILE_KEY(fp?.id)
  )
  const { goToPreviousStep, goToNextStep } = useTaxProfileSteps(
    TAX_PROFILE_STEP.ten99Income
  )
  const replacementText = useTherapistFlag(CaseStyle.titleCase, true)

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      hasOtherIncome: formFlowAnswers.otherIndividualIncome ? true : undefined,
      otherIndividualIncome: formFlowAnswers.otherIndividualIncome,
    },
    onSubmit: async ({ hasOtherIncome, otherIndividualIncome }) => {
      const res = await dispatch(
        updateFinancialProfile(fp?.id, {
          otherIndividualIncomeInCents: hasOtherIncome
            ? dollarsToCents(otherIndividualIncome)
            : null,
        })
      )

      if (res) {
        setFormFlowAnswers({
          ...formFlowAnswers,
          otherIndividualIncome,
        })
        track('1099 income submitted', {
          has_1099_income: hasOtherIncome ?? false,
          ten99_income_in_cents: hasOtherIncome
            ? dollarsToCents(otherIndividualIncome)
            : null,
          user_id: fp?.userId ?? null,
        })
        goToNextStep(res)
      }
    },
  })

  const { values, validateForm, submitForm, isSubmitting, isValid } = formik

  const faqCopy = useTaxProfileAccordionCopy()

  return (
    <FormikProvider value={formik}>
      <div className="w-full space-y-6">
        <Text
          variant={TypographyStyles.DISPLAY_LG}
          className="max-w-3xl mx-auto text-center"
        >
          {isScorp
            ? 'Do you earn 1099 income?'
            : 'Do you earn additional income?'}
        </Text>

        {/* Subtext */}
        <Text
          variant={TypographyStyles.BODY_MD}
          className="max-w-3xl mx-auto text-center"
        >
          {replacementText} typically earn additional 1099 income through jobs
          like supervision, consulting, speaking, etc.
          {!isScorp && (
            <>
              {' '}
              This income is earned outside of your private practice and{' '}
              <b>isn’t already recorded in Heard.</b>
            </>
          )}
        </Text>

        {error && (
          <Alert
            className="max-w-3xl mx-auto"
            variant="error"
            description={error.message}
          />
        )}

        <div className="max-w-2xl mx-auto">
          <FormikLabelError
            name={getFieldName<typeof values>('hasOtherIncome')}
            schema={makeReqBoolSchema()}
          />
        </div>

        {/* Radio buttons */}
        <div className="max-w-2xl mx-auto flex flex-col sm:flex-row justify-center gap-4">
          <div className="sm:w-1/2 w-full">
            <FormikRadioToggleButton
              name={getFieldName<typeof values>('hasOtherIncome')}
              value
              fullWidth
              onClick={validateForm}
            >
              Yes
            </FormikRadioToggleButton>
          </div>
          <div className="sm:w-1/2 w-full">
            <FormikRadioToggleButton
              name={getFieldName<typeof values>('hasOtherIncome')}
              value={false}
              fullWidth
              onClick={validateForm}
            >
              No
            </FormikRadioToggleButton>
          </div>
        </div>

        {/* Input if Yes */}
        {values.hasOtherIncome && (
          <div className="max-w-2xl mx-auto">
            <Form.Input
              name={getFieldName<typeof values>('otherIndividualIncome')}
              required
              componentType="currency"
              schema={makeNumberSchema({
                allowedDecimals: 2,
                greaterThanZero: true,
              })}
              label="Year-to-date 1099 income from other jobs"
              description={
                <>
                  This is money earned this year through contractor positions,
                  short-term gigs, or other services.
                  {!isScorp && (
                    <>
                      {' '}
                      Only include 1099 income that <b>isn’t in Heard.</b>
                    </>
                  )}
                </>
              }
            />
          </div>
        )}

        {/* FAQs */}
        <TaxProfileAccordions
          faqs={[faqCopy.needToInclude1099Job, faqCopy.howFind1099]}
        />

        {/* Footer */}
        <TaxProfileFormFooter
          submitOrContinue={submitForm}
          goToPreviousStep={goToPreviousStep}
          continueDisabled={!isValid || isSubmitting}
          loading={isSubmitting}
        />
      </div>
    </FormikProvider>
  )
}
